// The 6502 as BASIC runs it (web/microsoft-basic/page.js): a diagram of the processor's parts, not
// its die, each lit by how much of a frame's instructions used it, and its registers and flags as
// the frame leaves them, the program counter, the buses and the decoder at the next instruction, as
// DDT lists it. Every light is the instructions the 6502 (../cpu.js) has just run, counted as it
// runs them: nothing is made up for the show.
//
//   const chip = watch(cpu, read)   counts each instruction as the CPU runs it (cpu.trace)
//   chip.frame()                    the frame's counts and the registers, then counting afresh
//   const panel = diagram(el)       the diagram drawn into el; panel.show(chip.frame())
import { OPS } from '@moonbase-machines/mos6502/disasm.js';

// which parts an instruction uses, by its mnemonic and its addressing mode
const ALU = new Set('ADC SBC AND ORA EOR CMP CPX CPY BIT ASL LSR ROL ROR INC DEC INX INY DEX DEY'.split(' '));
const USES = {
  a: new Set('LDA STA ADC SBC AND ORA EOR CMP BIT PHA PLA TAX TAY TXA TYA'.split(' ')),
  x: new Set('LDX STX CPX INX DEX TAX TXA TSX TXS'.split(' ')),
  y: new Set('LDY STY CPY INY DEY TAY TYA'.split(' ')),
  s: new Set('PHA PLA PHP PLP JSR RTS RTI BRK TSX TXS'.split(' ')),
  pc: new Set('JMP JSR RTS RTI BRK BPL BMI BVC BVS BCC BCS BNE BEQ'.split(' ')),
  p: new Set('CLC SEC CLI SEI CLV CLD SED PHP PLP BPL BMI BVC BVS BCC BCS BNE BEQ'.split(' ')),
};
const MEMORY = new Set(['zp', 'zpx', 'zpy', 'abs', 'abx', 'aby', 'izx', 'izy']);
function parts(name, mode) {
  const out = [];
  for (const [k, set] of Object.entries(USES)) if (set.has(name)) out.push(k);
  if (ALU.has(name)) out.push('alu', 'p');
  if (mode === 'acc') out.push('a');
  if (mode === 'zpx' || mode === 'abx' || mode === 'izx') out.push('x');
  if (mode === 'zpy' || mode === 'aby' || mode === 'izy') out.push('y');
  if (MEMORY.has(mode)) out.push('memory');
  if (/^LD|^PL/.test(name)) out.push('p');
  return out;
}
const PARTS = Object.fromEntries(OPS.map((op, i) => [i, op.name === '???' ? [] : parts(op.name, op.mode)]));

export function watch(cpu, read) {
  let counts = {}, n = 0;
  cpu.trace = pc => {
    const op = read(pc);
    for (const p of PARTS[op]) counts[p] = (counts[p] ?? 0) + 1;
    n++;
  };
  return {
    frame() {
      // (the program counter, the buses and the decoder at the next instruction, where the
      // processor stands, as DDT lists it; the lights the instructions already run)
      const op = read(cpu.pc), f = { counts, n, pc: cpu.pc, op, name: OPS[op].name, mode: OPS[op].mode, a: cpu.a, x: cpu.x, y: cpu.y, s: cpu.sp, p: cpu.p };
      counts = {}; n = 0;
      return f;
    },
  };
}

const hex = (v, w) => v.toString(16).toUpperCase().padStart(w, '0');
const MODES = { imp: 'implied', acc: 'the accumulator', imm: 'immediate', zp: 'zero page', zpx: 'zero page,X', zpy: 'zero page,Y', izx: '(indirect,X)', izy: '(indirect),Y', rel: 'relative', abs: 'absolute', abx: 'absolute,X', aby: 'absolute,Y', ind: 'indirect' };

// the diagram: the registers across the top, the flags, the ALU and the decoder, the buses below;
// a part's light its share of the frame's instructions
export function diagram(el) {
  const box = (id, x, y, w, h, label, value = '') => `<g class="part" data-part="${id}"><rect x="${x}" y="${y}" width="${w}" height="${h}" rx="6"/><text class="label" x="${x + 8}" y="${y + 15}">${label}</text><text class="value" x="${x + w - 8}" y="${y + h - 9}" text-anchor="end" data-value="${id}">${value}</text></g>`;
  const dots = (id, x, y, n, label) => `<text class="label" x="${x}" y="${y - 8}">${label}</text>` + Array.from({ length: n }, (_, i) => `<circle class="bit" data-bit="${id}${n - 1 - i}" cx="${x + 6 + i * 15}" cy="${y + 6}" r="5"/>`).join('');
  const flags = 'NV-BDIZC'.split('').map((f, i) => `<g class="flag" data-flag="${7 - i}"><circle cx="${22 + i * 30}" cy="182" r="9"/><text x="${22 + i * 30}" y="186" text-anchor="middle">${f}</text></g>`).join('');
  el.innerHTML = `<svg viewBox="0 0 260 400" role="img" aria-label="The 6502's registers, flags, ALU and buses as BASIC runs">
    ${box('a', 8, 8, 76, 52, 'A', '00')}${box('x', 92, 8, 76, 52, 'X', '00')}${box('y', 176, 8, 76, 52, 'Y', '00')}
    ${box('s', 8, 68, 76, 52, 'Stack', '00')}${box('pc', 92, 68, 160, 52, 'Program counter', '0000')}
    <g class="part" data-part="p"><rect x="8" y="128" width="244" height="70" rx="6"/><text class="label" x="16" y="145">Flags</text>${flags}</g>
    ${box('alu', 8, 206, 120, 52, 'ALU', '')}${box('memory', 132, 206, 120, 52, 'Memory', '')}
    <g class="part decoder"><rect x="8" y="266" width="244" height="46" rx="6"/><text class="label" x="16" y="281">Decoder</text><text class="value op" x="244" y="302" text-anchor="end" data-value="op"></text></g>
    ${dots('ad', 8, 336, 16, 'Address bus')}${dots('db', 8, 378, 8, 'Data bus')}
    <text class="label rate" x="252" y="378" text-anchor="end" data-value="rate"></text>
  </svg>`;
  const q = s => el.querySelector(s), parts = [...el.querySelectorAll('[data-part]')];
  return {
    show(f) {
      for (const g of parts) { const k = g.dataset.part, share = f.n ? (f.counts[k] ?? 0) / f.n : 0; g.style.setProperty('--lit', share ? (0.18 + 0.82 * Math.min(1, share * 1.6)).toFixed(2) : 0); }
      q('[data-value="a"]').textContent = hex(f.a, 2); q('[data-value="x"]').textContent = hex(f.x, 2); q('[data-value="y"]').textContent = hex(f.y, 2);
      q('[data-value="s"]').textContent = hex(f.s, 2); q('[data-value="pc"]').textContent = hex(f.pc, 4);
      q('[data-value="op"]').textContent = `${f.name} ${MODES[f.mode] ?? ''}`.trim();
      q('[data-value="rate"]').textContent = f.paused ? 'paused by DDT' : f.n ? `${f.n.toLocaleString()} instructions` : 'waiting for a key';
      for (const c of el.querySelectorAll('[data-flag]')) c.classList.toggle('on', !!(f.p >> +c.dataset.flag & 1));
      for (const c of el.querySelectorAll('[data-bit]')) { const id = c.dataset.bit, bus = id.slice(0, 2), i = +id.slice(2); c.classList.toggle('on', !!((bus === 'ad' ? f.pc : f.op) >> i & 1)); }
    },
  };
}
